import { Crop } from "react-image-crop";

export interface ImageSize {
  width: number;
  height: number;
}

interface FlipOptions {
  horizontal: boolean;
  vertical: boolean;
}

export const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous"); // needed to avoid cross-origin issues on CodeSandbox
    image.src = url;
  });

export function getRadianAngle(degreeValue: number): number {
  return (degreeValue * Math.PI) / 180;
}

/**
 * Returns the new bounding area of a rotated rectangle.
 */
export function rotateSize(
  width: number,
  height: number,
  rotation: number,
): ImageSize {
  const rotRad = getRadianAngle(rotation);

  return {
    width:
      Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height:
      Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

/**
 * This function was adapted from the one in the ReadMe of https://github.com/DominicTobias/react-image-crop
 * You can provide a transform function to decide how you want to transform the blob. By default, it will return a base64 URL.
 * @see {@link getCroppedImgAsFile}, a convenience function that will return a File object
 */
export async function getCroppedImg<OutType = string>(
  imageSrc: string,
  pixelCrop: Crop,
  rotation = 0,
  displayedImageSize?: ImageSize,
  flip: FlipOptions = { horizontal: false, vertical: false },
  transform: (blob: Blob) => OutType = URL.createObjectURL as (
    blob: Blob,
  ) => OutType,
): Promise<OutType> {
  const image = await createImage(imageSrc);

  // Crop UI coords are in displayed CSS pixels; export at natural resolution
  // so ArtMatch search gets the full-quality image (not a max-h ~512px downscale).
  const naturalWidth = image.naturalWidth || image.width;
  const naturalHeight = image.naturalHeight || image.height;
  const displayedWidth = displayedImageSize?.width ?? naturalWidth;
  const displayedHeight = displayedImageSize?.height ?? naturalHeight;
  const scaleX = naturalWidth / displayedWidth;
  const scaleY = naturalHeight / displayedHeight;

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) throw new Error("Failed to get canvas context");

  const rotRad = getRadianAngle(rotation);

  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
    naturalWidth,
    naturalHeight,
    rotation,
  );

  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
  ctx.translate(-naturalWidth / 2, -naturalHeight / 2);

  ctx.drawImage(image, 0, 0, naturalWidth, naturalHeight);

  const croppedCanvas = document.createElement("canvas");

  const croppedCtx = croppedCanvas.getContext("2d");

  if (!croppedCtx) throw new Error("Failed to get cropped canvas context");

  const cropX = pixelCrop.x * scaleX;
  const cropY = pixelCrop.y * scaleY;
  const cropWidth = pixelCrop.width * scaleX;
  const cropHeight = pixelCrop.height * scaleY;

  croppedCanvas.width = Math.max(1, Math.round(cropWidth));
  croppedCanvas.height = Math.max(1, Math.round(cropHeight));

  croppedCtx.imageSmoothingEnabled = true;
  croppedCtx.imageSmoothingQuality = "high";
  croppedCtx.drawImage(
    canvas,
    cropX,
    cropY,
    cropWidth,
    cropHeight,
    0,
    0,
    croppedCanvas.width,
    croppedCanvas.height,
  );

  return new Promise((resolve) => {
    croppedCanvas.toBlob((file) => {
      if (!file) throw new Error("Failed to convert cropped canvas to blob");
      resolve(transform(file));
    }, "image/png");
  });
}

export async function getCroppedImgAsFile(
  imageSrc: string,
  pixelCrop: Crop,
  displayedImageSize: ImageSize,
  rotation = 0,
  flip: FlipOptions = { horizontal: false, vertical: false },
) {
  return getCroppedImg(
    imageSrc,
    pixelCrop,
    rotation,
    displayedImageSize,
    flip,
    (file) => new File([file], "croppedImage.jpg"),
  );
}

/** Default max pixel dimension before a resample is offered (matches the API guard). */
export const MAX_IMAGE_DIMENSION = 2000;

/** Scales dimensions down so neither edge exceeds `max`, preserving aspect ratio. */
export function clampDimensionsToMax(
  width: number,
  height: number,
  max: number = MAX_IMAGE_DIMENSION,
): ImageSize {
  if (width <= max && height <= max) {
    return { width, height };
  }
  const ratio = width / height;
  if (width >= height) {
    return { width: max, height: Math.round(max / ratio) };
  }
  return { width: Math.round(max * ratio), height: max };
}

export function exceedsImageDimensionLimit(
  width: number,
  height: number,
  max: number = MAX_IMAGE_DIMENSION,
): boolean {
  return width > max || height > max;
}

export interface ResampleOptions {
  /** Lock the width to this value; height is derived from the aspect ratio. */
  lockWidth?: number;
  /** Lock the height to this value; width is derived from the aspect ratio. */
  lockHeight?: number;
  /** Output quality for lossy formats (0..1). Default 0.92. */
  quality?: number;
  /** Output mime type. Default "image/jpeg". */
  mimeType?: string;
  /**
   * Painted under the image before it is drawn. Required when encoding a
   * transparent source (PNG, WebP) to a format without alpha, which would
   * otherwise turn the transparent areas black.
   */
  background?: string;
}

/** Reads the natural pixel dimensions of an image source. */
export async function getImageDimensions(imageSrc: string): Promise<ImageSize> {
  const image = await createImage(imageSrc);
  return { width: image.naturalWidth, height: image.naturalHeight };
}

/**
 * Downscales (resamples — never crops) an image by locking either its width or
 * its height; the other dimension is recomputed to preserve the aspect ratio.
 * Never upscales. Returns a File ready to upload.
 */
export async function resampleImage(
  imageSrc: string,
  options: ResampleOptions,
  fileName = "resampledImage.jpg",
): Promise<File> {
  const image = await createImage(imageSrc);
  const {
    lockWidth,
    lockHeight,
    quality = 0.92,
    mimeType = "image/jpeg",
    background,
  } = options;

  const naturalWidth = image.naturalWidth;
  const naturalHeight = image.naturalHeight;
  const ratio = naturalWidth / naturalHeight;

  let targetWidth = naturalWidth;
  let targetHeight = naturalHeight;

  if (lockWidth && lockWidth > 0) {
    targetWidth = Math.min(lockWidth, naturalWidth); // no upscale
    targetHeight = Math.round(targetWidth / ratio);
  } else if (lockHeight && lockHeight > 0) {
    targetHeight = Math.min(lockHeight, naturalHeight); // no upscale
    targetWidth = Math.round(targetHeight * ratio);
  }

  const canvas = document.createElement("canvas");
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Failed to get canvas context");

  if (background) {
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, targetWidth, targetHeight);
  }

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(image, 0, 0, targetWidth, targetHeight);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) return reject(new Error("Failed to resample image"));
        resolve(new File([blob], fileName, { type: mimeType }));
      },
      mimeType,
      quality,
    );
  });
}

/** Result of an automatic downscale performed just before an upload. */
export interface AutoResampleResult {
  /** The file to upload: the original one when no downscale was needed. */
  file: File;
  /** True when the file was actually resampled. */
  resized: boolean;
  /** Dimensions of `file` (original ones when `resized` is false). */
  width: number;
  height: number;
}

/** Canvas can only re-encode these; anything else falls back to JPEG. */
const CANVAS_ENCODABLE_MIME_TYPES = ["image/png", "image/jpeg", "image/webp"];

const EXTENSION_BY_MIME: Record<string, string> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp",
};

/** Rewrites a file name so its extension matches the encoded mime type. */
function withExtensionForMime(fileName: string, mimeType: string): string {
  const extension = EXTENSION_BY_MIME[mimeType];
  if (!extension) return fileName;
  const base = fileName.replace(/\.[^./\\]+$/, "") || "image";
  return `${base}.${extension}`;
}

/**
 * Prepares a picked file for an endpoint that enforces a dimension limit
 * (favicon, logo du certificat): it downscales so neither edge exceeds `max`
 * and, when `mimeType` is given, re-encodes to that format even if the size
 * already fits. Those endpoints reject an oversized or wrongly-typed file
 * outright, which left the user to convert it by hand elsewhere.
 *
 * The aspect ratio is preserved and the file is never upscaled. Without an
 * explicit `mimeType` the source format is kept whenever the canvas can
 * re-encode it, so a transparent PNG stays a PNG. Returns the original file
 * untouched when nothing has to change.
 */
export async function resampleFileToMaxDimension(
  file: File,
  max: number = MAX_IMAGE_DIMENSION,
  options: { mimeType?: string; background?: string } = {},
): Promise<AutoResampleResult> {
  const sourceUrl = URL.createObjectURL(file);
  try {
    const { width, height } = await getImageDimensions(sourceUrl);
    const mimeType =
      options.mimeType ??
      (CANVAS_ENCODABLE_MIME_TYPES.includes(file.type)
        ? file.type
        : "image/jpeg");
    const target = clampDimensionsToMax(width, height, max);
    const resized = target.width !== width || target.height !== height;
    if (!resized && mimeType === file.type) {
      return { file, resized: false, width, height };
    }

    const resampled = await resampleImage(
      sourceUrl,
      { lockWidth: target.width, mimeType, background: options.background },
      withExtensionForMime(file.name, mimeType),
    );

    return {
      file: resampled,
      resized,
      width: target.width,
      height: target.height,
    };
  } finally {
    URL.revokeObjectURL(sourceUrl);
  }
}

export async function getRotatedImage(
  imageSrc: string,
  rotation = 0,
): Promise<string | null> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    return null;
  }

  const orientationChanged =
    rotation === 90 ||
    rotation === -90 ||
    rotation === 270 ||
    rotation === -270;
  if (orientationChanged) {
    canvas.width = image.height;
    canvas.height = image.width;
  } else {
    canvas.width = image.width;
    canvas.height = image.height;
  }

  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.drawImage(image, -image.width / 2, -image.height / 2);

  return new Promise((resolve) => {
    canvas.toBlob((file) => {
      if (file) {
        resolve(URL.createObjectURL(file));
      } else {
        resolve(null);
      }
    }, "image/png");
  });
}
